import {
  Button,
  Grid,
  GridItem,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  NumberInput,
  NumberInputField,
  Stack,
  Text,
} from '@chakra-ui/react';

import { ChevronDownIcon } from '@chakra-ui/icons';
import type { CustomField } from '@/client/types/AuthConfig';
import type { UserDetailsFormData } from '@/client/components/learners/profile/overlay/LearnerProfileUserDetailsFormModal';
import { capCase } from '@/client/utils/capCase';
import { useAuthStore } from '@/client/services/state/authStore';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default function LearnerProfileCustomFieldsForm() {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { company } = authConfig;
  const { user_metadata_fields: userMetadataFields } = company;

  const {
    watch,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
    trigger,
  } = useFormContext<UserDetailsFormData>();
  const { metadata } = watch();

  // Filter custom fields that should be visible in profile
  const visibleCustomFields =
    userMetadataFields
      ?.filter(
        (field) =>
          field.visibility.profile === 'readonly' || field.visibility.profile === 'editable',
      )
      .sort((fieldA, fieldB) => fieldA.name.localeCompare(fieldB.name)) || [];

  // Dynamic validation function for custom fields
  const validateCustomField = (value: any, field: CustomField) => {
    // Use validation_rules if available, fallback to validationRules
    const validationRules = (field as any).validation_rules || field.validationRules;

    if (!validationRules) {
      return true;
    }

    const rules = validationRules;

    // Check if value is empty or null
    const isEmpty = !value || value.toString().trim() === '';

    // Required validation - only check if field is required
    if (rules.required && isEmpty) {
      return t('customFields.requiredField');
    }

    // If the field is not required and the value is empty, don't validate further
    if (!rules.required && isEmpty) {
      return true;
    }

    // Min length validation - only if value is not empty
    if (rules.minLength && !isEmpty && value.toString().length < rules.minLength) {
      return t('customFields.minLength', { minLength: rules.minLength });
    }

    // Max length validation - only if value is not empty
    if (rules.maxLength && !isEmpty && value.toString().length > rules.maxLength) {
      return t('customFields.maxLength', { maxLength: rules.maxLength });
    }

    // No numbers validation - only if value is not empty
    if (rules.noNumbers && !isEmpty) {
      const numberRegex = /\d/;
      if (numberRegex.test(value.toString())) {
        return t('customFields.noNumbers');
      }
    }

    // No symbols validation - only if value is not empty
    if (rules.noSymbols && !isEmpty) {
      const symbolsRegex = /[^a-zA-Z0-9]/;
      if (symbolsRegex.test(value.toString())) {
        return t('customFields.noSpecialCharacters');
      }
    }

    // Pattern validation - only if value is not empty
    if (rules.pattern && !isEmpty && !new RegExp(rules.pattern).test(value.toString())) {
      return rules.patternMessage || t('customFields.invalidFormat');
    }

    // Number range validation - only if value is not empty
    const dataType = (field as any).data_type || field.dataType;
    if (dataType === 'number' && !isEmpty) {
      const numValue = Number(value);

      if (Number.isNaN(numValue)) {
        return t('customFields.mustBeValidNumber');
      }
      if (rules.min !== undefined && numValue < rules.min) {
        return t('customFields.minimumValue', { min: rules.min });
      }
      if (rules.max !== undefined && numValue > rules.max) {
        return t('customFields.maximumValue', { max: rules.max });
      }
    }

    return true;
  };

  const handleFieldChange = (fieldName: string, value: any, field: CustomField) => {
    // Ensure cleared fields are set to empty string instead of null/undefined/NaN
    const normalizedValue =
      value === null || value === undefined || value === '' || Number.isNaN(value) ? '' : value;

    // Update the metadata object while preserving the required Nickname property
    const updatedMetadata = {
      ...metadata,
      [fieldName]: normalizedValue,
    };

    setValue('metadata', updatedMetadata, { shouldDirty: true, shouldValidate: false });

    // Validate the field and set/clear errors accordingly
    const validationResult = validateCustomField(normalizedValue, field);

    if (validationResult === true) {
      // Clear any existing error for this field
      clearErrors(`metadata.${fieldName}` as any);

      // Force form to re-evaluate validity after clearing errors
      setTimeout(() => {
        trigger('metadata');
      }, 0);
    } else {
      // Set error for this specific field within metadata
      setError(`metadata.${fieldName}` as any, {
        type: 'custom',
        message: validationResult,
      });
    }

    // Force form validation to update isValid state
    // trigger('metadata'); // This was clearing the errors
  };

  const getFieldValue = (fieldName: string) => (metadata as any)?.[fieldName] || '';

  const getFieldError = (fieldName: string) => {
    // Check if there's a validation error for this specific field within metadata
    const metadataErrors = errors.metadata as any;
    if (metadataErrors && metadataErrors[fieldName]) {
      return metadataErrors[fieldName].message;
    }

    return undefined;
  };

  const renderBooleanMenu = (field: CustomField) => {
    const currentValue = getFieldValue(field.name);
    const fieldError = getFieldError(field.name);

    const options = [
      { value: 'true', label: t('global.forms.labels_true') },
      { value: 'false', label: t('global.forms.labels_false') },
    ];

    const selectedOption = options.find((opt) => opt.value === currentValue);

    return (
      <Menu>
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon color="baseBlack" />}
          backgroundColor="white"
          color={selectedOption ? 'baseBlack' : 'neutral.500'}
          fontSize="14px"
          fontWeight={500}
          paddingY="14px"
          paddingX={3}
          border="1px solid"
          borderColor={fieldError ? 'red.500' : 'warmNeutral.200'}
          borderRadius="6px"
          textAlign="left"
          _hover={{ backgroundColor: 'warmNeutral.0' }}
          _active={{ backgroundColor: 'warmNeutral.0' }}
          _focus={{ borderColor: fieldError ? 'red.500' : 'warmNeutral.300' }}
        >
          {selectedOption
            ? selectedOption.label
            : t('customFields.select', { field: capCase(field.name) })}
        </MenuButton>

        <MenuList
          border="1px solid"
          borderColor="warmNeutral.200"
          borderRadius="6px"
          boxShadow="0px 4px 12px 0px rgba(0, 0, 0, 0.15)"
          paddingX={4}
          maxHeight="200px"
          overflowY="auto"
        >
          {options.map((option, index) => (
            <MenuItem
              key={option.value}
              value={option.value}
              color="baseBlack"
              fontSize="14px"
              fontWeight={500}
              borderBottom={index !== options.length - 1 ? '1px solid' : 'none'}
              borderBottomColor="neutral.100"
              paddingY={4}
              _hover={{ backgroundColor: 'warmNeutral.0' }}
              onClick={() => handleFieldChange(field.name, option.value, field)}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    );
  };

  const renderInput = (field: CustomField) => {
    const currentValue = getFieldValue(field.name);
    const fieldError = getFieldError(field.name);

    // Use data_type if available, fallback to dataType
    const dataType = (field as any).data_type || field.dataType;

    switch (dataType) {
      case 'number': {
        const numberRules = field.validationRules;
        const minValue = numberRules?.min;
        const maxValue = numberRules?.max;

        return (
          <NumberInput
            value={currentValue || ''}
            onChange={(_, valueAsNumber) => {
              handleFieldChange(field.name, valueAsNumber, field);
            }}
            onBlur={() => {
              // Optional: Add any blur-specific logic here
            }}
            variant="create"
            min={minValue}
            max={maxValue}
          >
            <NumberInputField
              placeholder={`${t('customFields.enterNumber')}${
                minValue !== undefined && maxValue !== undefined
                  ? t('customFields.enterNumber.minMax', { min: minValue, max: maxValue })
                  : minValue !== undefined
                  ? t('customFields.enterNumber.min', { min: minValue })
                  : maxValue !== undefined
                  ? t('customFields.enterNumber.max', { max: maxValue })
                  : ''
              }`}
              maxLength={8}
              borderColor={fieldError ? 'red.500' : 'warmNeutral.200'}
              _focus={{ borderColor: fieldError ? 'red.500' : 'warmNeutral.600' }}
            />
          </NumberInput>
        );
      }

      case 'boolean':
        return renderBooleanMenu(field);

      default:
        return (
          <Input
            value={currentValue || ''}
            onChange={(e) => {
              handleFieldChange(field.name, e.target.value, field);
            }}
            variant="warmNeutral"
            borderColor={fieldError ? 'red.500' : 'warmNeutral.200'}
            _focus={{ borderColor: fieldError ? 'red.500' : 'warmNeutral.300' }}
            placeholder={`${t('customFields.enterText', { field: capCase(field.name) })}`}
          />
        );
    }
  };

  if (!visibleCustomFields.length) {
    return null;
  }

  return (
    <Stack spacing={6}>
      <Text variant="formSectionHeading">{t('userProfileEdit.yourCustomFields')}</Text>

      <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }} gap={6}>
        {visibleCustomFields.map((field) => {
          const fieldError = getFieldError(field.name);

          return (
            <GridItem key={field.name}>
              <Stack spacing={2}>
                <Text variant="formlabel">{capCase(field.name)}</Text>

                {field.visibility.profile === 'editable' ? (
                  <Stack spacing={0}>
                    {renderInput(field)}

                    <Text variant="formErrorText">{fieldError || ' '}</Text>
                  </Stack>
                ) : (
                  <Text
                    fontSize="14px"
                    color="neutral.600"
                    paddingY="14px"
                    paddingX={3}
                    backgroundColor="white"
                    border="1px solid"
                    borderColor="warmNeutral.200"
                    borderRadius="6px"
                    display="flex"
                    alignItems="center"
                    height="40px"
                    cursor="not-allowed"
                  >
                    {getFieldValue(field.name) || t('customFields.notSet')}
                  </Text>
                )}
              </Stack>
            </GridItem>
          );
        })}
      </Grid>
    </Stack>
  );
}
